import React, { useEffect, useState, useCallback } from "react";
import {
  Search,
  Filter,
  Plus,
  ShieldCheck,
  Settings,
  Store as StoreIcon,
  Users,
  KeySquare,
  Printer,
  Sidebar as SidebarIconLucide,
  Trophy,
  Save,
  RotateCcw,
  LayoutGrid,
  SlidersHorizontal,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import StoreConfigTable from "../components/StoreConfigTable";
import UserDataTable from "../components/UserDataTable";
import AddUserModal from "../components/AddUserModal";
import EditConfigModal from "../components/EditConfigModal";
import PrinterConfigTab from "../components/PrinterConfigTab";
import ScannerConfigTab from "../components/ScannerConfigTab";
import OtherSettingsTab from "../components/OtherSettingsTab";
import { configService } from "../api/configService";
import { useNotification } from "../contexts/NotificationContext";
import { useSidebarConfig } from "../contexts/SidebarConfigContext";
import {
  getReceiptConfig,
  setReceiptConfig,
} from "../utils/receiptConfig";
import { QrCode } from "lucide-react";

const TABS = [
  { label: "Konfigurasi Toko", icon: StoreIcon },
  { label: "Data Pengguna", icon: Users },
  { label: "Konfigurasi Printer", icon: Printer },
  { label: "Konfigurasi Scanner", icon: QrCode },
  { label: "Pengaturan Lainnya", icon: SlidersHorizontal },
];

export default function Configuration() {
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const { resetConfig: resetSidebarConfig } = useSidebarConfig();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isAdmin] = useState(() => {
    try {
      const data = JSON.parse(localStorage.getItem("user_data") || "{}");
      return data?.is_admin === true;
    } catch {
      return false;
    }
  });
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";

  const availableTabs = React.useMemo(() => {
    let tabs = isAdmin ? TABS : TABS.filter(
      (t) => t.label === "Konfigurasi Printer" || t.label === "Konfigurasi Scanner" || t.label === "Pengaturan Lainnya"
    );
    if (isApps) {
      tabs = tabs.filter((t) => t.label !== "Konfigurasi Scanner");
    }
    return tabs;
  }, [isAdmin, isApps]);

  const [activeTab, setActiveTab] = useState(isAdmin ? "Konfigurasi Toko" : "Konfigurasi Printer");
  const [searchTerm, setSearchTerm] = useState("");

  // Modals
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [isRoleDetailModalOpen, setIsRoleDetailModalOpen] = useState(false);
  const [isEditConfigModalOpen, setIsEditConfigModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [, setLoadingTab] = useState(false);
  const [, setErrorTab] = useState("");

  // Mock Data for Store Config
  const [storeConfigData, setStoreConfigData] = useState([]);

  // Mock Data for Users
  const [userData, setUserData] = useState([]);


  const [storeId, setStoreId] = useState("");
  const [storeSlug, setStoreSlug] = useState("");
  const [storeRaw, setStoreRaw] = useState(null);

  // === Printer config (state lifted up so Save button can live in navbar) ===
  const [printerConfig, setPrinterConfig] = useState(() => getReceiptConfig());
  const [printerInitial, setPrinterInitial] = useState(() => getReceiptConfig());
  const [printerFooterNote, setPrinterFooterNote] = useState("");
  const [printerFooterInitial, setPrinterFooterInitial] = useState("");
  const [savingPrinter, setSavingPrinter] = useState(false);

  const printerDirty =
    JSON.stringify(printerConfig) !== JSON.stringify(printerInitial) ||
    printerFooterNote !== printerFooterInitial;

  const [selectedUser, setSelectedUser] = useState(null);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsAddUserModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    if (!user?.id) return;
    if (window.confirm("Apakah Anda yakin ingin menghapus user ini?")) {
      configService
        .deleteUser(user.id)
        .then(() => {
          showSuccess("User berhasil dihapus");
          loadUsers();
        })
        .catch((err) => {
          showError(err.message || "Gagal menghapus user");
        });
    }
  };

  const handleResetPassword = (user) => {
    if (!user?.id) return;
    if (window.confirm(`Apakah Anda yakin ingin mereset password untuk user ${user.name} menjadi "123456"?`)) {
      configService
        .updateUser(user.id, { password: "123456" })
        .then(() => {
          showSuccess("Password berhasil direset menjadi 123456");
        })
        .catch((err) => {
          showError(err.message || "Gagal mereset password");
        });
    }
  };

  const handleSaveUser = (user) => {
    const toPayload = (u, isCreate) => {
      const base = {
        full_name: u.name,
        email: u.email,
        phone: u.phone,
        role_id: u.role_id,
        job_title: u.position,
      };
      // Gunakan password dari form, atau biarkan kosong jika edit (tidak diubah)
      if (u.password) {
        base.password = u.password;
      } else if (isCreate && u.phone) {
        // Fallback: Default password = nomor handphone untuk user baru jika form kosong
        base.password = u.phone;
      }
      return Object.fromEntries(
        Object.entries(base).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      );
    };
    if (user.id) {
      const payload = toPayload(user, false);
      configService
        .updateUser(user.id, payload)
        .then(() => {
          showSuccess("User berhasil diperbarui");
          setIsAddUserModalOpen(false);
          setSelectedUser(null);
          loadUsers();
        })
        .catch((err) => {
          showError(err.message || "Gagal memperbarui user");
        });
    } else {
      const payload = toPayload(user, true);
      configService
        .inviteUser(payload)
        .then(() => {
          showSuccess(
            "User berhasil ditambahkan",
          );
          setIsAddUserModalOpen(false);
          setSelectedUser(null);
          loadUsers();
        })
        .catch((err) => {
          showError(err.message || "Gagal menambahkan user");
        });
    }
  };

  const handleEditRole = (role) => {
    const raw = rolesRaw.find((r) => r.id === role.id) || role;
    setSelectedRole(raw);
    setIsAddRoleModalOpen(true);
  };

  const handleDeleteRole = (role) => {
    if (!role?.id) return;
    if (window.confirm("Apakah Anda yakin ingin menghapus role ini?")) {
      configService
        .deleteRole(role.id)
        .then(() => {
          showSuccess("Role berhasil dihapus");
          loadRoles();
        })
        .catch((err) => {
          showError(err.message || "Gagal menghapus role");
        });
    }
  };

  const handleSaveRole = (rolePayload) => {
    // Basic validation before sending
    if (!rolePayload?.name || !rolePayload.name.trim()) {
      showError("Nama role wajib diisi");
      return;
    }
    const payload = {
      name: rolePayload.name.trim(),
    };
    if (rolePayload.description && rolePayload.description.trim()) {
      payload.description = rolePayload.description.trim();
    }
    // permission_ids harus selalu dikirim sebagai array (boleh kosong)
    payload.permission_ids = Array.isArray(rolePayload.permission_ids)
      ? rolePayload.permission_ids
      : [];

    const action = selectedRole?.id
      ? configService.updateRole(selectedRole.id, payload)
      : configService.createRole(payload);
    action
      .then(() => {
        showSuccess(
          selectedRole?.id
            ? "Role berhasil diperbarui"
            : "Role berhasil dibuat",
        );
        setIsAddRoleModalOpen(false);
        setSelectedRole(null);
        loadRoles();
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          (selectedRole?.id
            ? "Gagal memperbarui role"
            : "Gagal membuat role");
        showError(msg);
      });
  };

  const handleEditStoreConfig = (item) => {
    setSelectedConfig(item);
    setIsEditConfigModalOpen(true);
  };

  const handleSaveConfig = (updatedConfig) => {
    const base = {
      id: storeId || undefined,
      slug: storeSlug || undefined,
      name:
        updatedConfig.label === "Nama Toko"
          ? updatedConfig.value
          : (storeRaw?.name ?? ""),
      address:
        updatedConfig.label === "Alamat Toko"
          ? updatedConfig.value
          : (storeRaw?.settings?.address ?? storeRaw?.address ?? ""),
      phone:
        updatedConfig.label === "Nomor HP"
          ? updatedConfig.value
          : (storeRaw?.settings?.phone ?? storeRaw?.phone ?? ""),
      footer_note: storeRaw?.settings?.footer_note ?? "",
      settings: {
        ...(storeRaw?.settings || {}),
        address:
          updatedConfig.label === "Alamat Toko"
            ? updatedConfig.value
            : (storeRaw?.settings?.address ?? storeRaw?.address ?? ""),
        phone:
          updatedConfig.label === "Nomor HP"
            ? updatedConfig.value
            : (storeRaw?.settings?.phone ?? storeRaw?.phone ?? ""),
      }
    };

    // Handle logo
    if (updatedConfig.label === "Logo Toko") {
      if (updatedConfig.isLogo && updatedConfig.value) {
        // Extract base64 data and content type from data URL
        const matches = updatedConfig.value.match(/^data:([^;]+);base64,(.+)$/);
        if (matches) {
          base.logo_base64 = matches[2];
          base.logo_content_type = matches[1];
        }
      } else if (updatedConfig.value === "") {
        // Hapus logo (dikirim melalui settings agar backend mereset URL)
        base.settings = { logo_url: "" };
      } else {
        base.settings = { logo_url: updatedConfig.value };
      }
    }

    const payload = Object.entries(base).reduce((acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== "") acc[k] = v;
      if (k === "settings") acc[k] = v; // Allow settings object to pass through
      return acc;
    }, {});
    configService
      .updateStore(payload)
      .then((res) => {
        showSuccess(
          updatedConfig.label === "Logo Toko"
            ? "Logo toko berhasil diperbarui"
            : "Konfigurasi toko berhasil diperbarui",
        );
        setIsEditConfigModalOpen(false);

        if (base.name) {
          localStorage.setItem("tenantName", base.name);
        }
        if (base.address) {
          localStorage.setItem("tenantAddress", base.address);
        }
        window.dispatchEvent(new Event("storage"));
        window.dispatchEvent(new CustomEvent("tenantUpdated", { detail: base }));

        loadStore();
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Gagal memperbarui konfigurasi toko";
        showError(msg);
      });
  };

  const handleAdd = () => {
    if (activeTab === "Data Pengguna") {
      setSelectedUser(null);
      setIsAddUserModalOpen(true);
    }
  };

  const handleViewRoleDetail = (role) => {
    const raw = rolesRaw.find((r) => r.id === role.id) || role;
    setSelectedRole(raw);
    setIsRoleDetailModalOpen(true);
  };

  function mapStoreToItems(store) {
    const updatedBy = store?.updated_by_name || store?.updated_by || "Sistem";
    const updatedAt = store.updated_at || "";
    // Footer Nota dipindahkan ke tab Konfigurasi Printer (B.11)
    return [
      {
        id: 1,
        label: "Nama Toko",
        value: store.name || "",
        type: "text",
        updatedBy,
        updatedAt,
      },
      {
        id: 2,
        label: "Alamat Toko",
        value: store.settings?.address || store.address || "",
        type: "textarea",
        updatedBy,
        updatedAt,
      },
      {
        id: 3,
        label: "Nomor HP",
        value: store.settings?.phone || store.phone || "",
        type: "text",
        updatedBy,
        updatedAt,
      },
      {
        id: 4,
        label: "Logo Toko",
        value: store.logo_url || "",
        type: "image",
        updatedBy,
        updatedAt,
      },
    ];
  }

  function mapUsersToRows(rows) {
    return rows.map((u) => {
      let roleName = u.role;
      if (u.is_system_admin || !roleName || roleName.trim() === "") {
        if (u.is_system_admin) {
          roleName = "Superadmin";
        } else if (u.role_name) {
          roleName = u.role_name;
        } else if (u.job_title && (u.job_title.toLowerCase().includes("admin") || u.job_title.toLowerCase().includes("super"))) {
          roleName = "Superadmin";
        } else {
          roleName = u.role || "-";
        }
      }

      let roleIdVal = u.role_id;
      if (!roleIdVal) {
        if (u.is_system_admin || roleName.toLowerCase() === "superadmin") {
          roleIdVal = "superadmin";
        } else if (roleName.toLowerCase() === "kasir") {
          roleIdVal = "kasir";
        } else {
          roleIdVal = roleName;
        }
      }

      return {
        id: u.id,
        name: u.full_name,
        position: u.job_title,
        role: roleName,
        role_id: roleIdVal,
        email: u.email,
        phone: u.phone,
        status: u.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
        raw: u,
      };
    });
  }

  function mapRolesToRows(rows) {
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      access:
        Array.isArray(r.permissions) && Object.keys(permissionsRef).length
          ? r.permissions.map((p) => {
            const mod = Object.keys(permissionsRef).find((m) =>
              permissionsRef[m].some(
                (x) => x.slug === p.slug || x.id === p.id,
              ),
            );
            return mod || "Akses";
          })
          : [],
      description: r.description || "",
      createdBy: r.is_default ? "System" : "",
      createdAt: "",
    }));
  }

  const loadStore = useCallback(() => {
    setLoadingTab(true);
    setErrorTab("");
    configService
      .getStore()
      .then((data) => {
        setStoreId(data?.id || "");
        setStoreSlug(data?.slug || "");
        setStoreRaw(data || null);
        setStoreConfigData(mapStoreToItems(data));
        const fn = data?.settings?.footer_note || "";
        setPrinterFooterNote(fn);
        setPrinterFooterInitial(fn);
        // Cache tenant info & dispatch event for real-time header sync
        const name = data?.name || "";
        const address = data?.settings?.address || data?.address || "";
        const logoUrl = data?.logo_url || data?.settings?.logo_url || "";
        try {
          if (name) localStorage.setItem("tenantName", name);
          if (address) {
            localStorage.setItem("tenantAddress", address);
          } else {
            localStorage.removeItem("tenantAddress");
          }
          if (logoUrl) {
            localStorage.setItem("tenantLogoUrl", logoUrl);
          } else {
            localStorage.removeItem("tenantLogoUrl");
          }
          window.dispatchEvent(new Event("storage"));
          window.dispatchEvent(new CustomEvent("tenantUpdated", { detail: data }));
        } catch { }
      })
      .catch((err) => {
        const errMsg = String(err.message || err);
        setErrorTab(errMsg);
        showError(errMsg || "Gagal memuat data toko");
      })
      .finally(() => setLoadingTab(false));
  }, [showError]);

  const loadUsers = useCallback(() => {
    setLoadingTab(true);
    setErrorTab("");
    configService
      .getUsers()
      .then((data) => setUserData(mapUsersToRows(data)))
      .catch((err) => {
        const errMsg = String(err.message || err);
        setErrorTab(errMsg);
        showError(errMsg || "Gagal memuat data user");
      })
      .finally(() => setLoadingTab(false));
  }, [showError]);

  const rolesOptions = [
    { id: 'superadmin', name: 'Superadmin' },
    { id: 'kasir', name: 'Kasir' }
  ];

  // Pre-load store on mount agar dropdown di AddUserModal sudah siap (B.6)
  useEffect(() => {
    loadStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy load per tab
  useEffect(() => {
    if (activeTab === "Konfigurasi Toko") loadStore();
    if (activeTab === "Data Pengguna") loadUsers();
  }, [activeTab, loadStore, loadUsers]);

  // === Save handlers for tab-specific actions in navbar ===
  const handleSavePrinter = async () => {
    setSavingPrinter(true);
    try {
      setReceiptConfig(printerConfig);
      setPrinterInitial(printerConfig);

      // Footer note dikirim ke backend jika berubah
      if (printerFooterNote !== printerFooterInitial) {
        const payload = {
          id: storeId || undefined,
          slug: storeSlug || undefined,
          name: storeRaw?.name || "",
          address: storeRaw?.settings?.address || storeRaw?.address || "",
          phone: storeRaw?.settings?.phone || storeRaw?.phone || "",
          footer_note: printerFooterNote,
          logo_url: storeRaw?.logo_url || "",
        };
        const cleanPayload = Object.entries(payload).reduce((acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== "") acc[k] = v;
          return acc;
        }, {});
        await configService.updateStore(cleanPayload);
        setPrinterFooterInitial(printerFooterNote);
        loadStore();
      }

      showSuccess("Pengaturan printer berhasil disimpan");
    } catch (err) {
      showError(err.message || "Gagal menyimpan pengaturan printer");
    } finally {
      setSavingPrinter(false);
    }
  };

  const handleResetSidebar = () => {
    resetSidebarConfig();
    showSuccess("Pengaturan sidebar dikembalikan ke default");
  };

  const showManajemenAktivasi = activeTab === "Konfigurasi Toko"; // B.5

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="konfigurasi"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-8">
          {/* Top Card: Page Title & Actions */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-5 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left: Page Title */}
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                  <Settings className="w-6 h-6 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    Konfigurasi
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Pengaturan toko dan akses pengguna
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2.5 w-full md:w-auto flex-wrap justify-end">
                {/* Manajemen Aktivasi (B.5 - hanya pada Konfigurasi Toko) */}
                {showManajemenAktivasi && (
                  <>
                    <div className="px-3.5 py-2 bg-teal-50 text-teal-700 ring-1 ring-teal-200 rounded-lg text-sm font-medium">
                      Aktif Sampai 9 April 2027
                    </div>
                    <button
                      onClick={() =>
                        navigate("/konfigurasi/manajemen-aktivasi")
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg text-sm font-medium transition-colors shadow-sm shadow-teal-600/20"
                      title="Buka halaman Manajemen Aktivasi">
                      <ShieldCheck className="w-4 h-4" />
                      Manajemen Aktivasi
                    </button>
                  </>
                )}

                {/* Tab-specific action: Save (Printer) - B.9 */}
                {activeTab === "Konfigurasi Printer" && (
                  <button
                    type="button"
                    onClick={handleSavePrinter}
                    disabled={!printerDirty || savingPrinter}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${printerDirty && !savingPrinter
                      ? "bg-teal-600 text-white hover:bg-teal-700 shadow-teal-600/20"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                      }`}>
                    <Save className="w-4 h-4" />
                    {savingPrinter ? "Menyimpan..." : "Simpan Pengaturan"}
                  </button>
                )}

                {/* Tab-specific action: Reset (Sidebar) - B.13 */}
                {activeTab === "Konfigurasi Sidebar" && (
                  <button
                    type="button"
                    onClick={handleResetSidebar}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
                    title="Kembalikan ke default">
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                )}

                {/* Search + Filter + Tambah for table tabs */}
                {(activeTab === "Data Pengguna") && (
                  <>
                    <div className="relative md:w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Cari data . . ."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white"
                      />
                    </div>

                    <button className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 text-slate-500 hover:text-slate-700 transition-colors">
                      <Filter className="w-5 h-5" />
                    </button>

                    <button
                      onClick={handleAdd}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors whitespace-nowrap shadow-sm shadow-teal-600/20">
                      <Plus className="w-4 h-4" />
                      <span>Tambah</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation (Reports pill style) - B.2 */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-3 mb-6 shadow-sm">
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex items-center min-w-max gap-2">
                {availableTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.label;
                  return (
                    <button
                      key={tab.label}
                      onClick={() => setActiveTab(tab.label)}
                      className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${isActive
                        ? "bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/30 ring-1 ring-teal-500"
                        : "bg-slate-50 text-slate-600 ring-1 ring-transparent hover:text-teal-700 hover:bg-teal-50 hover:ring-teal-200"
                        }`}>
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${isActive
                          ? "bg-white/20"
                          : "bg-white text-teal-600 group-hover:bg-teal-100"
                          }`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: { duration: 0.2, ease: "easeOut" },
              }}
              exit={{
                opacity: 0,
                y: 8,
                scale: 0.98,
                transition: { duration: 0.15, ease: "easeIn" },
              }}>
              {activeTab === "Konfigurasi Toko" && (
                <StoreConfigTable
                  data={storeConfigData}
                  onEdit={handleEditStoreConfig}
                />
              )}

              {activeTab === "Data Pengguna" && (
                <UserDataTable
                  data={userData}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  onResetPassword={handleResetPassword}
                />
              )}



              {activeTab === "Konfigurasi Printer" && (
                <PrinterConfigTab
                  config={printerConfig}
                  onChange={setPrinterConfig}
                  footerNote={printerFooterNote}
                  onFooterNoteChange={setPrinterFooterNote}
                  storeName={storeRaw?.name || "Toko"}
                  storeAddress={storeRaw?.settings?.address || storeRaw?.address || ""}
                  storePhone={storeRaw?.settings?.phone || storeRaw?.phone || ""}
                  storeLogoUrl={storeRaw?.logo_url || storeRaw?.settings?.logo_url || ""}
                />
              )}

              {activeTab === "Konfigurasi Scanner" && <ScannerConfigTab />}

              {activeTab === "Pengaturan Lainnya" && <OtherSettingsTab />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        initialData={selectedUser}
        onSave={handleSaveUser}
        roles={rolesOptions}
      />



      <EditConfigModal
        isOpen={isEditConfigModalOpen}
        onClose={() => setIsEditConfigModalOpen(false)}
        data={selectedConfig}
        onSave={handleSaveConfig}
      />
    </div>
  );
}



